// Campaigns page: list view + monthly calendar view
import { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon, List, Edit3, Save, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_LABELS = {
  pending_start: "Pendiente de inicio", active: "Activa", ending_soon: "Por vencer",
  finished: "Finalizada", renewed: "Renovada", cancelled: "Cancelada", paused: "Pausada",
};
const STATUS_COLORS = {
  pending_start: "bg-zinc-500", active: "bg-emerald-500", ending_soon: "bg-amber-500",
  finished: "bg-blue-500", renewed: "bg-violet-500", cancelled: "bg-red-500", paused: "bg-orange-500",
};

export default function CampaignsPage() {
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [programs, setPrograms] = useState([]);

  const load = () => api.get("/admin/com/campaigns").then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/admin/programs").then(setPrograms).catch(() => setPrograms([]));
  }, []);

  const save = async () => {
    const body = { ...editing };
    body.pending_amount = Math.max(0, Number(body.contracted_amount || 0) - Number(body.paid_amount || 0));
    await api.put(`/admin/com/campaigns/${editing.id}`, body);
    setEditing(null); load();
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar campaña?")) return;
    try { await api.delete(`/admin/com/campaigns/${id}`); load(); } catch (e) { alert("Error: " + (e.response?.data?.detail || "")); }
  };

  return (
    <div data-testid="commercial-campaigns">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Campañas</h1>
          <p className="text-sm text-muted-foreground">{items.length} campañas · auto-creadas al cerrar propuestas</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView("list")} className={`px-4 h-10 rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 ${view === "list" ? "bg-primary text-primary-foreground" : "border border-border"}`}><List className="w-4 h-4" /> Lista</button>
          <button onClick={() => setView("calendar")} className={`px-4 h-10 rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 ${view === "calendar" ? "bg-primary text-primary-foreground" : "border border-border"}`}><CalendarIcon className="w-4 h-4" /> Calendario</button>
        </div>
      </div>

      {view === "list" && (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-3">Código</th><th className="text-left p-3">Cliente / Marca</th><th className="text-left p-3">Programa</th><th className="text-left p-3">Inicio</th><th className="text-left p-3">Fin</th><th className="text-right p-3">Contratado</th><th className="text-right p-3">Pagado</th><th className="text-right p-3">Pendiente</th><th className="text-left p-3">Estado</th><th className="text-right p-3">Acciones</th></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3 font-mono text-xs">{c.code}</td>
                  <td className="p-3"><strong>{c.client_name}</strong><br/><span className="text-xs text-muted-foreground">{c.brand_name}</span></td>
                  <td className="p-3 text-xs">{c.program_name || "—"}</td>
                  <td className="p-3 text-xs">{c.start_date || "—"}</td>
                  <td className="p-3 text-xs">{c.end_date || "—"}</td>
                  <td className="p-3 text-right">{c.currency} {(c.contracted_amount || 0).toLocaleString()}</td>
                  <td className="p-3 text-right text-emerald-500">{c.currency} {(c.paid_amount || 0).toLocaleString()}</td>
                  <td className="p-3 text-right text-amber-500">{c.currency} {(c.pending_amount || 0).toLocaleString()}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm text-white ${STATUS_COLORS[c.status] || "bg-secondary"}`}>{STATUS_LABELS[c.status] || c.status}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing({ ...c })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-1 hover:border-primary"><Edit3 className="w-3 h-3" /></button>
                    <button onClick={() => del(c.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">Sin campañas. Se crean automáticamente al marcar propuestas como "Cerrada".</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "calendar" && <CalendarView items={items} year={year} month={month} setYear={setYear} setMonth={setMonth} />}

      {editing && <CampaignEditor c={editing} setC={setEditing} programs={programs} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CampaignEditor({ c, setC, programs, onSave, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-end p-4" onClick={onClose}>
      <div className="bg-background w-full max-w-xl h-full overflow-y-auto rounded-sm border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-black text-2xl">{c.code}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          {[
            ["client_name", "Cliente"], ["brand_name", "Marca"], ["agency_name", "Agencia"],
          ].map(([k, l]) => (
            <div key={k} className="col-span-2 md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">{l}</label>
              <input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" />
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Programa</label>
            <select value={c.program_id || ""} onChange={(e) => {
              const p = programs.find((x) => x.id === e.target.value);
              setC({ ...c, program_id: e.target.value, program_name: p?.name || "" });
            }} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
              <option value="">— Ninguno —</option>
              {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Inicio</label><input type="date" value={c.start_date || ""} onChange={(e) => setC({ ...c, start_date: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" /></div>
          <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Fin</label><input type="date" value={c.end_date || ""} onChange={(e) => setC({ ...c, end_date: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" /></div>
          <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Estado</label>
            <select value={c.status} onChange={(e) => setC({ ...c, status: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Frecuencia</label><input value={c.frequency || ""} placeholder="Diario, Semanal..." onChange={(e) => setC({ ...c, frequency: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" /></div>
          <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Contratado</label><input type="number" value={c.contracted_amount || 0} onChange={(e) => setC({ ...c, contracted_amount: Number(e.target.value) })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" /></div>
          <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Pagado</label><input type="number" value={c.paid_amount || 0} onChange={(e) => setC({ ...c, paid_amount: Number(e.target.value) })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs" /></div>
          <div><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Moneda</label>
            <select value={c.currency || "USD"} onChange={(e) => setC({ ...c, currency: e.target.value })} className="w-full h-9 px-2 bg-background border border-border rounded-sm text-xs">
              <option value="USD">USD</option><option value="BOB">BOB</option>
            </select>
          </div>
          <div className="col-span-2"><label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Observaciones</label><textarea rows={3} value={c.observations || ""} onChange={(e) => setC({ ...c, observations: e.target.value })} className="w-full px-2 py-2 bg-background border border-border rounded-sm text-xs resize-none" /></div>
          <button onClick={onSave} className="col-span-2 inline-flex items-center justify-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
        </div>
      </div>
    </div>
  );
}

function CalendarView({ items, year, month, setYear, setMonth }) {
  const days = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startWeek = first.getDay(); // 0=Sun
    const lastDay = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeek; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const itemsByDay = useMemo(() => {
    const map = {};
    items.forEach((c) => {
      if (!c.start_date) return;
      const start = c.start_date.slice(0, 10);
      const end = c.end_date?.slice(0, 10) || start;
      // mark each day between
      const sd = new Date(start), ed = new Date(end);
      for (let d = new Date(sd); d <= ed; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year && d.getMonth() + 1 === month) {
          const day = d.getDate();
          (map[day] = map[day] || []).push(c);
        }
      }
    });
    return map;
  }, [items, year, month]);

  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  return (
    <div className="border border-border rounded-sm">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <button onClick={() => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); }} className="px-3 py-1.5 border border-border rounded-sm text-xs">←</button>
        <h3 className="font-display font-black text-xl">{monthNames[month - 1]} {year}</h3>
        <button onClick={() => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); }} className="px-3 py-1.5 border border-border rounded-sm text-xs">→</button>
      </div>
      <div className="grid grid-cols-7 border-b border-border bg-secondary/30">
        {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => <div key={d} className="p-2 text-[10px] uppercase tracking-widest font-bold text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => (
          <div key={i} className={`min-h-24 border-r border-b border-border p-1.5 ${d ? "bg-background" : "bg-secondary/10"}`}>
            {d && <p className="text-[10px] font-bold mb-1">{d}</p>}
            {(itemsByDay[d] || []).slice(0, 3).map((c) => (
              <div key={c.id} className={`text-[10px] mb-1 px-1.5 py-0.5 rounded-sm text-white truncate ${STATUS_COLORS[c.status] || "bg-zinc-500"}`} title={`${c.client_name} · ${c.brand_name}`}>
                {c.brand_name || c.client_name}
              </div>
            ))}
            {(itemsByDay[d] || []).length > 3 && <p className="text-[9px] text-muted-foreground">+{itemsByDay[d].length - 3} más</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
